import React, { useEffect, useRef, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/asset-task-ui.css";
import { useAppContext } from "../context/AppContext";
import {
  getAssetTaskById,
  updateAssetTask,
  getProcessTasks,
} from "../services/assetTaskService";

import { toast } from "../utils/toast";
import ConfirmModal from "../components/ui/ConfirmModal";
import { getAssetTasksByProcessId } from "../services/assetTaskService";
import { useParams } from "react-router-dom";
import { resolveRoleName } from "../utils/auth";

const AssetTaskEditPage = () => {
  const { assetTaskId, setAssetTaskId, setActiveSection } = useAppContext();
  const { assetTaskId: assetTaskIdParam } = useParams();

  const [chainTasks, setChainTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");


  const [originalAssetTask, setOriginalAssetTask] = useState(null);

  const [form, setForm] = useState({
    task_id: "",
    process_id: "",
    type: "information",
    input: false,
    output: false,
    description: "",
    document_link: "",
  });

  const didInit = useRef(false);
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);
  const pendingUpdateRef = useRef(null);
  const [propagateModal, setPropagateModal] = useState({
    open: false,
    targetId: null,
    busy: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  useEffect(() => {
    (async () => {
      if (didInit.current) return;
      didInit.current = true;

      try {
        setLoading(true);

        let id = assetTaskId;
        if (assetTaskIdParam) {
          const decoded = decodeURIComponent(assetTaskIdParam);
          id = decoded;
          try {
            localStorage.setItem("activeAssetTaskId", String(decoded));
          } catch {}
          setAssetTaskId(decoded);
        }
        if (!id) {
          try {
            const stored = localStorage.getItem("activeAssetTaskId");
            if (stored) {
              id = stored;
              setAssetTaskId(stored);
            }
          } catch {}
        }

        if (!id) throw new Error("Missing asset task id");

        const data = await getAssetTaskById(id);
        setOriginalAssetTask(data);

        if (data) {
          setForm({
            task_id: data.task_id != null ? String(data.task_id) : "",
            process_id: data.process_id != null ? String(data.process_id) : "",
            type: data.type || "information",
            input: Boolean(data.input),
            output: Boolean(data.output),
            description: data.description || "",
            document_link: data.document_link || "",
          });

          // Load chain if process exists
          if (data.process_id) {
            try {
              const chainData = await getProcessTasks(data.process_id);
              if (Array.isArray(chainData)) {
                setChainTasks(chainData);
              }
            } catch (e) {
              console.error("Failed to load process chain:", e);
            }
          }
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load asset task");
        setError(e?.message || "Failed to load asset task");
      } finally {
        setLoading(false);
      }
    })();
  }, [assetTaskId, assetTaskIdParam, setAssetTaskId]);

  const onBack = () => setActiveSection("asset-task-management");

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const getTaskName = () => {
    return originalAssetTask?.task?.task_name || `Task #${form.task_id}`;
  };

  const getProcessName = () => {
    return (
      originalAssetTask?.process?.process_name || `Process #${form.process_id}`
    );
  };

  const canSave = () => {
    return (
      String(form.task_id).trim() &&
      String(form.process_id).trim() &&
      String(form.type).trim() &&
      String(form.description).trim()
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSave() || saving) return;

    setError("");

    try {
      // Only allow changing type and description; task/process selection is required
      const payload = {
        task_id: Number(form.task_id),
        process_id: form.process_id ? Number(form.process_id) : null,
        type: form.type,
        description: form.description.trim(),
        document_link:
          form.type === "document" && String(form.document_link || "").trim() ?
            String(form.document_link).trim()
          : null,
      };

      // Determine if we should prompt to propagate changes to the next task's input
      const isOutput = !!form.output;
      const hasProcess = String(form.process_id).trim();

      // Ensure we have chain info; fetch if missing or index not found
      let localChain = Array.isArray(chainTasks) ? chainTasks : [];
      if (!hasProcess) localChain = [];
      if (
        hasProcess &&
        (localChain.length === 0 ||
          !localChain.some((ct) => Number(ct.task_id) === Number(form.task_id)))
      ) {
        try {
          const fetched = await getProcessTasks(Number(form.process_id));
          if (Array.isArray(fetched) && fetched.length > 0) {
            localChain = fetched;
            // do not force setChainTasks synchronously; use local reference for decision
          }
        } catch {}
      }

      const currentIdx = localChain.findIndex(
        (ct) => Number(ct.task_id) === Number(form.task_id),
      );
      const hasNext = currentIdx >= 0 && currentIdx < localChain.length - 1;

      if (isOutput && hasProcess && hasNext) {
        try {
          const nextTaskId = localChain[currentIdx + 1].task_id;
          const list = await getAssetTasksByProcessId(Number(form.process_id));
          const arr = Array.isArray(list) ? list : [];
          const nextInput = arr.find(
            (a) => Number(a.task_id) === Number(nextTaskId) && a.input,
          );
          if (nextInput && nextInput.asset_task_id) {
            // Store pending update and show modal
            pendingUpdateRef.current = { payload };
            setPropagateModal({
              open: true,
              targetId: nextInput.asset_task_id,
              busy: false,
            });
            return; // Wait for user choice
          }
        } catch (err) {
          // Non-blocking: if check fails, just proceed with normal update
        }
      }

      setSaving(true);
      let id = assetTaskId;
      if (!id) {
        try {
          id = localStorage.getItem("activeAssetTaskId");
        } catch {}
      }
      if (!id) throw new Error("Asset task ID not found");
      await updateAssetTask(id, payload);
      toast.success("Asset task updated successfully");
      setActiveSection("asset-task-management");
    } catch (e) {
      setError(e?.message || "Failed to update asset task");
      toast.error(e?.message || "Failed to update asset task");
    } finally {
      setSaving(false);
    }
  };

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Edit Asset Task</h2>
            <p className="page-subtitle">
              Update asset task details and configuration
            </p>
          </div>
          <div className="roles-toolbar">
            <button
              className="secondary-btn"
              onClick={onBack}
              disabled={saving}
            >
              Back
            </button>
            <button
              className="primary-btn"
              onClick={onSubmit}
              disabled={!canSave() || saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {error && <div className="error-banner">{error}</div>}
        {loading ?
          <div className="no-results">Loading...</div>
        : <form onSubmit={onSubmit}>
            <div className="asset-form-card">
              <div className="card-header">
                <div className="card-header-icon">
                  <svg
                    style={{ width: "16px", height: "16px" }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </div>
                <h3 className="card-header-title">Asset Details</h3>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <div className="readonly-field-label">
                    <svg
                      className="readonly-icon"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    Process
                  </div>
                  <input
                    type="text"
                    className="readonly-field"
                    value={getProcessName()}
                    disabled
                  />
                  <small className="field-hint">
                    Process cannot be changed while editing.
                  </small>
                </div>

                <div className="form-group">
                  <div className="readonly-field-label">
                    <svg
                      className="readonly-icon"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    Task
                  </div>
                  <input
                    type="text"
                    className="readonly-field"
                    value={getTaskName()}
                    disabled
                  />
                  <small className="field-hint">
                    Task is fixed and cannot be changed.
                  </small>
                </div>

                <div className="form-group">
                  <div className="readonly-field-label">
                    <svg
                      className="readonly-icon"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    Position
                  </div>
                  <div
                    style={{
                      padding: "10px 12px",
                      background: "var(--gray-50)",
                      border: "1px solid var(--gray-200)",
                      borderRadius: "6px",
                    }}
                  >
                    <span
                      className={`position-badge ${
                        form.input ? "input"
                        : form.output ? "output"
                        : ""
                      }`}
                    >
                      <svg
                        style={{ width: "14px", height: "14px" }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        {form.input ?
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16l-4-4m0 0l4-4m-4 4h18"
                          />
                        : <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        }
                      </svg>
                      {form.input && form.output ?
                        "Input & Output"
                      : form.input ?
                        "Input"
                      : form.output ?
                        "Output"
                      : "-"}
                    </span>
                  </div>
                  <small className="field-hint">
                    Flow position is view-only.
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    Type <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) => setField("type", e.target.value)}
                    required
                  >
                    <option value="event">Event</option>
                    <option value="information">Information</option>
                    <option value="document">Document</option>
                  </select>
                  <small className="field-hint">
                    Event: triggers/notifications | Information: data processing
                    | Document: file generation
                  </small>
                </div>

                {form.type === "document" && (
                  <div className="form-group">
                    <label>Document Link</label>
                    <input
                      type="url"
                      value={form.document_link}
                      onChange={(e) =>
                        setField("document_link", e.target.value)
                      }
                      placeholder="https://example.com/document.pdf"
                    />
                    <small className="field-hint">
                      Provide a URL for document-type assets.
                    </small>
                  </div>
                )}

                <div className="form-group full">
                  <label>
                    Description <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setField("description", e.target.value)}
                    rows={4}
                    placeholder="Describe this asset task..."
                    required
                  />
                  <small className="field-hint">
                    Describe what this asset task represents.
                  </small>
                </div>

                {chainTasks.length > 0 && (
                  <div className="form-group full">
                    <label>
                      <svg
                        style={{
                          width: "16px",
                          height: "16px",
                          display: "inline",
                          marginRight: "6px",
                        }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                      Process Task Chain
                    </label>
                    <div className="chain-display">
                      {chainTasks.map((ct, idx) => (
                        <React.Fragment key={idx}>
                          <div className="chain-step">
                            <div className="chain-step-number">
                              {ct.order || idx + 1}
                            </div>
                            <div className="chain-step-content">
                              <div className="chain-step-title">
                                {getTaskName(ct.task_id)}
                              </div>
                            </div>
                          </div>
                          {idx < chainTasks.length - 1 && (
                            <div className="chain-arrow">↓</div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <small className="field-hint">
                      Tasks in this process workflow
                    </small>
                  </div>
                )}
              </div>
            </div>
          </form>
        }
      </div>

      {propagateModal.open && (
        <ConfirmModal
          open={propagateModal.open}
          title="Update Next Task Input?"
          message={
            "This asset is an Output. The next task in this process has an Input linked from this Output. Do you want to apply the same changes (type, description, document link) to that Input as well?"
          }
          confirmLabel="Yes, update both"
          cancelLabel="No, just this one"
          onConfirm={async () => {
            if (propagateModal.busy) return;
            try {
              setPropagateModal((s) => ({ ...s, busy: true }));
              // First update current asset
              let id = assetTaskId;
              if (!id) {
                try {
                  id = localStorage.getItem("activeAssetTaskId");
                } catch {}
              }
              if (!id) throw new Error("Asset task ID not found");

              const { payload } = pendingUpdateRef.current || { payload: null };
              if (!payload) throw new Error("Missing pending payload");

              await updateAssetTask(id, payload);

              // Now update the next input asset
              const mirror = {
                type: payload.type,
                description: payload.description,
                document_link:
                  payload.type === "document" && payload.document_link ?
                    payload.document_link
                  : undefined,
              };
              await updateAssetTask(propagateModal.targetId, mirror);

              toast.success("Asset task updated. Next input updated as well.");
              setActiveSection("asset-task-management");
            } catch (err) {
              toast.error(err?.message || "Failed to update linked input");
              setPropagateModal({ open: false, targetId: null, busy: false });
            } finally {
              setPropagateModal({ open: false, targetId: null, busy: false });
            }
          }}
          onCancel={async () => {
            if (propagateModal.busy) return;
            try {
              setPropagateModal((s) => ({ ...s, busy: true }));
              // Only update current asset
              let id = assetTaskId;
              if (!id) {
                try {
                  id = localStorage.getItem("activeAssetTaskId");
                } catch {}
              }
              if (!id) throw new Error("Asset task ID not found");
              const { payload } = pendingUpdateRef.current || { payload: null };
              if (!payload) throw new Error("Missing pending payload");
              await updateAssetTask(id, payload);
              toast.success("Asset task updated successfully");
              setActiveSection("asset-task-management");
            } catch (err) {
              toast.error(err?.message || "Failed to update asset task");
            } finally {
              setPropagateModal({ open: false, targetId: null, busy: false });
            }
          }}
          busy={propagateModal.busy}
        />
      )}
    </div>
  );
};

export default AssetTaskEditPage;
